/**
 * QualityRating Component
 *
 * Surfaces the brand's WhatsApp account health to the marketer in plain
 * language — no scores, no ratios. Two-state design:
 *
 *   GREEN  → low-key pill: "✅ Cuenta saludable · te quedan N envíos hoy"
 *   YELLOW → warning banner: tells them why and what to do
 *   RED    → critical alert: stronger framing + clear next action
 *
 * The underlying Meta metrics (score 0-100 + daily limit + used today)
 * are still consumed via props so we can drive different copy without
 * coupling the page to Meta-specific numbers.
 */

import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

export type QualityTier = "green" | "yellow" | "red" | "unknown";

interface QualityRatingProps {
  tier: QualityTier;
  /** Score 0-100 — kept for the future "Ver detalles" link only */
  score?: number;
  /** Daily messaging limit assigned by Meta */
  dailyLimit?: number;
  /** Used today */
  usedToday?: number;
}

export function QualityRating({
  tier,
  dailyLimit = 10000,
  usedToday = 0,
}: QualityRatingProps) {
  const remaining = Math.max(dailyLimit - usedToday, 0);
  const remainingFormatted = remaining.toLocaleString("es-AR");

  /* ─── Green: low-key informational pill ─── */
  if (tier === "green") {
    return (
      <div className="flex items-center gap-2.5 px-4 py-2.5 bg-emerald-50/60 border border-emerald-100 rounded-xl">
        <CheckCircle2
          size={16}
          className="text-emerald-600 flex-shrink-0"
        />
        <p className="text-xs text-slate-700 leading-tight">
          <span className="font-semibold text-emerald-700">
            Tu cuenta de WhatsApp está saludable.
          </span>{" "}
          <span className="text-slate-500">
            Podés enviar hasta{" "}
            <span className="font-semibold text-slate-700">
              {remainingFormatted}
            </span>{" "}
            mensajes más hoy.
          </span>
        </p>
      </div>
    );
  }

  /* ─── Yellow: warning, actionable ─── */
  if (tier === "yellow") {
    return (
      <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
        <AlertTriangle
          size={18}
          className="text-amber-600 mt-0.5 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900 leading-tight">
            Tu cuenta tiene algunas quejas recientes
          </p>
          <p className="text-xs text-amber-700 mt-1 leading-relaxed">
            Cuidá la calidad de tus próximas campañas (segmentos bien
            elegidos, mensajes claros) para no perder capacidad de envío.
            Hoy te quedan{" "}
            <span className="font-semibold">{remainingFormatted}</span>{" "}
            mensajes.
          </p>
        </div>
      </div>
    );
  }

  /* ─── Red: critical, must act now ─── */
  if (tier === "red") {
    return (
      <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
        <XCircle
          size={18}
          className="text-red-600 mt-0.5 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-900 leading-tight">
            Tu cuenta está en riesgo de bloqueo
          </p>
          <p className="text-xs text-red-700 mt-1 leading-relaxed">
            Pausá los envíos masivos y respondé las conversaciones
            pendientes. Si la situación no mejora, Meta puede suspender el
            número en los próximos días.
          </p>
        </div>
      </div>
    );
  }

  /* ─── Unknown: nothing rendered to avoid noise ─── */
  return null;
}

export default QualityRating;
